import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from "framer-motion";
import { FaGoogle, FaApple, FaFacebook } from "react-icons/fa";
import { login, fetchMe, loginAppLearner, fetchLearnerMe } from '@/api/auth'
import { authGoogle } from '@/api/auth'
import { useGoogleLogin } from '@react-oauth/google'
import { AlertCircle, CheckCircle2, Eye, EyeOff, ShieldX } from "lucide-react";

export default function LoginPage() {
    const nav = useNavigate()
    const [q] = useSearchParams()
    const next = q.get('next') || ''
    const orgQ = q.get('orgId') || ''
    const runQ = q.get('runId') || ''

    const [err, setErr] = useState('');
    const [ok, setOk] = useState('');
    const routeAfterLearnerLogin = () => {
        if (next && next.startsWith('/')) nav(next, { replace: true })
        else if (orgQ && runQ) nav(`/play/${orgQ}/${runQ}`, { replace: true })
        else nav('/learn/home', { replace: true })
    }
    const [checking, setChecking] = useState(true)
    const [step, setStep] = useState(1);
    const [direction, setDirection] = useState(1);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false)

    const [socialNotice, setSocialNotice] = useState('');
    const [expiredModal, setExpiredModal] = useState(false);
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);

    const isSubExpired = (e) =>
        e?.response?.status === 403 &&
        String(e?.response?.data?.detail || '').toLowerCase() === 'subscription_expired';

    useEffect(() => {
        if (step === 2) {
            setTimeout(() => {
                passwordRef.current?.focus();
            }, 350);
            localStorage.clear();
        }

        if (step === 1) {
            setTimeout(() => {
                usernameRef.current?.focus();
            }, 100);
        }
        fetchMe()
            .then(() => nav('/dashboard', { replace: true }))
            .catch(async () => {
                try {
                    await fetchLearnerMe()
                    if (next && next.startsWith('/')) nav(next, { replace: true })
                    else if (orgQ && runQ) nav(`/play/${orgQ}/${runQ}`, { replace: true })
                    else nav('/learn/home', { replace: true })
                } catch {
                    setChecking(false)
                } finally {
                    setChecking(false)
                }
            })
    }, [nav, step, next, orgQ, runQ]);



    const googleLogin = useGoogleLogin({
        flow: 'implicit', // หรือ 'auth-code' ถ้า backend รองรับ
        onSuccess: async (tokenResponse) => {
            try {
                const r = await authGoogle(tokenResponse.access_token);

                const data = r?.data || {};
                if (data.success) {
                    if (data.admin?.id) {
                        localStorage.setItem('viewMode', 'admin');
                        nav('/dashboard', { replace: true });
                        return;
                    } else if (data.learner?.id) {
                        localStorage.setItem('viewMode', 'user');
                        routeAfterLearnerLogin();
                        return;
                    }
                }
            } catch (eGoogle) {
                if (isSubExpired(eGoogle)) { setExpiredModal(true); return }
                setErr('ไม่สามารถเข้าสู่ระบบด้วย Google ได้');
            }
        },
        onError: () => {
            setErr('Google Login ล้มเหลว');
        }
    });

    const canContinue = username.trim().length > 0;
    const canLogin = password.trim().length > 0;

    const goNext = () => {
        setPassword("");
        setDirection(1);
        setStep(2);
    };

    const goBack = () => {
        setDirection(-1);
        setStep(1);
    };

    const handleLogin = async () => {
        setErr('');
        setOk('');
        try {
            await login(username.trim(), password);
            localStorage.setItem('viewMode', 'admin');
            nav('/dashboard', { replace: true });
            return;
        } catch (eAdmin) {
            if (isSubExpired(eAdmin)) { setExpiredModal(true); return }
            try {
                const r = await loginAppLearner(username.trim(), password);
                const data = r?.data || {};
                const org = data.org_id || data.user?.org_id || data.learner?.org_id;
                if (org) localStorage.setItem('orgId', org);
                const uid = data.learner_id || data.learner?.id || data.user?.id || data.id;
                if (uid) localStorage.setItem('userId', uid);
                localStorage.setItem('roles', JSON.stringify(['LEARNER']));
                localStorage.setItem('viewMode', 'user');
                routeAfterLearnerLogin()
                return
            } catch (eLearner) {
                if (isSubExpired(eLearner)) { setExpiredModal(true); return }
                const code = String(eLearner?.response?.data?.detail || eLearner?.response?.data?.message || eLearner?.response?.data?.error || '').toLowerCase()
                const st = eLearner?.response?.status
                if (code === 'invalid_credential' || st === 401) setErr('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง')
                else if (st === 429) setErr('พยายามเข้าสู่ระบบบ่อยเกินไป กรุณาลองใหม่ภายหลัง')
                else if (st >= 500) setErr('เซิร์ฟเวอร์มีปัญหา กรุณาลองใหม่ภายหลัง')
                else if (!eLearner?.response) setErr('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้')
                else setErr('เข้าสู่ระบบไม่สำเร็จ')
            }
        }
    }

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-primary via-info to-secondary p-4">
                <div className="w-full max-w-md bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl px-8 py-10 space-y-4 text-center">
                    <p className="animate-pulse text-gray-700">กำลังตรวจสอบสถานะเซสชั่น…</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-primary via-info to-secondary p-4">
            <div className="w-full max-w-md bg-white/90 backdrop-blur-xl text-neutral-800 rounded-2xl shadow-2xl p-6 space-y-6">


                <div className="text-center space-y-1">
                    <h1 className="text-2xl font-bold">
                        {step === 1 ? "เข้าสู่ระบบ หรือ สมัครสมาชิก" : "กรุณาใส่รหัสผ่าน"}
                    </h1>
                    <p className="text-sm text-neutral-500">
                        เข้าสู่ระบบเพื่อดำเนินการต่อ
                    </p>
                </div>

                <div className="space-y-2">
                    <label className="text-sm text-neutral-500">บัญชีผู้ใช้</label>
                    <input
                        ref={usernameRef}
                        value={username}
                        onChange={(e) => {
                            setUsername(e.target.value);
                            if (step === 2) setPassword("");
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && canContinue) {
                                setDirection(1);
                                setStep(2);
                            }
                        }}
                        className="w-full px-4 py-3 rounded-xl border border-neutral-300
             focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="กรุณาใส่บัญชีผู้ใช้"
                    />

                </div>

                <AnimatePresence mode="wait" custom={direction}>
                    {step === 1 && (
                        <motion.div
                            key="step1"
                            custom={direction}
                            variants={stepVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            transition={{ duration: 0.25, ease: "easeOut" }}
                            className="space-y-6"
                        >
                            <button
                                disabled={!canContinue}
                                onClick={goNext}
                                className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-secondary
                           text-white font-semibold shadow-lg disabled:opacity-50"
                            >
                                ดำเนินการต่อ
                            </button>

                            <Divider />

                            <div className="space-y-3">
                                <SocialButton
                                    icon={<FaGoogle />}
                                    label="เข้าสู่ระบบด้วย Google"
                                    onClick={() => googleLogin()}
                                />
                                <SocialButton
                                    icon={<FaApple />}
                                    label="เข้าสู่ระบบด้วย Apple"
                                    onClick={() => setSocialNotice('Apple Login กำลังอยู่ระหว่างการพัฒนา')}
                                />

                                <SocialButton
                                    icon={<FaFacebook />}
                                    label="เข้าสู่ระบบด้วย Facebook"
                                    onClick={() => setSocialNotice('Facebook Login กำลังอยู่ระหว่างการพัฒนา')}
                                />

                            </div>

                            <Link to="/register" className="w-full text-sm text-neutral-500 hover:text-primary underline text-center block">
                                สมัครสมาชิก
                            </Link>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div
                            key="step2"
                            custom={direction}
                            variants={stepVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            transition={{ duration: 0.25, ease: "easeOut" }}
                            className="space-y-4"
                        >
                            <div className="space-y-2">
                                <label className="text-sm text-neutral-500">รหัสผ่าน</label>

                                <div className="relative">
                                    <input
                                        ref={passwordRef}
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && canLogin) {
                                                handleLogin();
                                            }
                                        }}
                                        className="w-full px-4 py-3 pr-12 rounded-xl border border-neutral-300
                                        focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="กรุณาใส่รหัสผ่าน"
                                    />

                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700"
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>


                            <button
                                onClick={handleLogin}
                                disabled={!canLogin}
                                className="w-full py-3 rounded-xl bg-primary text-white
                           font-semibold disabled:opacity-50"
                            >
                                เข้าสู่ระบบ
                            </button>

                            <button
                                onClick={() => {
                                    setDirection(-1);
                                    setStep(1);
                                    setErr('');
                                    setOk('');
                                }}
                                className="w-full text-sm text-neutral-500 hover:text-primary underline"
                            >
                                ย้อนกลับ
                            </button>

                            <button
                                onClick={() => {
                                    nav('/forgot-password')
                                }}
                                className="w-full text-sm text-blue-300 hover:text-primary"
                            >
                                ลืมรหัสผ่าน
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {!!err && (
                    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                        <AlertCircle className="h-5 w-5 mt-0.5" />
                        <div className="text-sm">{err}</div>
                    </div>
                )}

                {!err && !!ok && (
                    <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                        <CheckCircle2 className="h-5 w-5 mt-0.5" />
                        <div className="text-sm">{ok}</div>
                    </div>
                )}

                {socialNotice && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-center text-amber-600 bg-amber-50 border border-amber-200
               rounded-xl px-4 py-2"
                    >
                        {socialNotice}
                    </motion.div>
                )}

            </div>

            {/* Subscription Expired Modal */}
            <AnimatePresence>
                {expiredModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
                        onClick={() => setExpiredModal(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center space-y-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                                <ShieldX className="w-7 h-7 text-red-500" />
                            </div>
                            <h2 className="text-xl font-bold text-neutral-800">บัญชีองค์กรหมดอายุ</h2>
                            <p className="text-sm text-neutral-500 leading-relaxed">
                                Subscription ขององค์กรหมดอายุแล้ว<br />
                                กรุณาติดต่อผู้ดูแลระบบเพื่อต่ออายุหรืออัปเกรดแผน
                            </p>
                            <div className="pt-2 space-y-2">
                                <button
                                    onClick={() => setExpiredModal(false)}
                                    className="w-full py-2.5 rounded-xl bg-primary text-white font-semibold hover:opacity-90 transition"
                                >
                                    รับทราบ
                                </button>
                                <a
                                    href="/pricing"
                                    className="block w-full py-2.5 rounded-xl border border-neutral-300 text-neutral-600 font-medium hover:bg-neutral-50 transition"
                                >
                                    ดูแผนราคา
                                </a>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

const stepVariants = {
    hidden: (direction) => ({
        opacity: 0,
        x: direction > 0 ? 40 : -40,
    }),
    visible: {
        opacity: 1,
        x: 0,
    },
    exit: (direction) => ({
        opacity: 0,
        x: direction > 0 ? -40 : 40,
    }),
};


function SocialButton({ icon, label, onClick, disabled }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-xl
                 border border-neutral-300 hover:bg-neutral-100 transition
                 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <span className="text-lg">{icon}</span>
            <span className="font-medium">{label}</span>
        </button>
    );
}


function Divider() {
    return (
        <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-neutral-300" />
            <span className="text-xs text-neutral-400">หรือ</span>
            <div className="flex-1 h-px bg-neutral-300" />
        </div>
    );
}
